<template>
   <Head>
      <title>{{ $t("meta.return_test.title") }}</title>
      <meta name="description" :content="$t('meta.return_test.content')" />
   </Head>

   <main id="render-element" class="my-20 overflow-hidden">
      <!-- Step index -->
      <div class="mx-auto mt-8 max-w-lg md:mt-16">
         <div class="container mx-auto flex max-w-xl flex-col text-center">
            <div class="mb-4">
               <router-link v-slot="{ href }" to="#step-one" custom>
                  <a
                     :href="href"
                     class="flex flex-col items-center justify-between rounded-lg bg-brand-dark p-6 text-white md:flex-row md:px-6 md:py-4"
                  >
                     <i18n-t keypath="dna-testing.take_test" class="text-lg" tag="span" scope="global">
                        <template v-slot:step>
                           <span class="text-regular block md:inline">{{
                              $t("dna-testing.step", { step_number: "1" })
                           }}</span>
                        </template>
                     </i18n-t>
                     <IconArrowShortLine class="mt-4 size-5 text-white md:mt-0" style="transform: rotate(90deg)" />
                  </a>
               </router-link>
            </div>
            <div class="mb-4">
               <router-link v-slot="{ href }" to="#step-two" custom>
                  <a
                     :href="href"
                     class="flex flex-col items-center justify-between rounded-lg bg-brand-dark p-6 text-white md:flex-row md:px-6 md:py-4"
                  >
                     <i18n-t keypath="dna-testing.return_sample" class="text-lg" tag="span" scope="global">
                        <template v-slot:step>
                           <span class="text-regular block md:inline">{{
                              $t("dna-testing.step", { step_number: "2" })
                           }}</span>
                        </template>
                     </i18n-t>
                     <IconArrowShortLine class="mt-4 size-5 text-white md:mt-0" style="transform: rotate(90deg)" />
                  </a>
               </router-link>
            </div>
         </div>
      </div>

      <!-- Step 1 - collect saliva sample guide -->
      <section id="step-one" class="container mx-auto pt-20 xl:px-4">
         <i18n-t
            keypath="dna-testing.watch_video"
            class="mx-auto mb-10 max-w-sm text-center text-xl md:text-3xl"
            tag="h2"
            scope="global"
         >
            <template v-slot:step>
               <strong>{{ $t("dna-testing.step", { step_number: "1" }) }}</strong>
            </template>
         </i18n-t>
         <YoutubeVideo
            videoId="jwXKwPuLx9Q"
            posterImageUrl="https://media.sanogenetics.com/pages/register-kit/video-poster-iii-800w.png"
         />
         <div class="mt-8 h-px w-full bg-grey-20 md:mt-20"></div>
      </section>

      <section id="step-two" class="container mx-auto pb-4 pt-16 text-brand-dark xl:px-4">
         <i18n-t
            class="mx-auto mb-6 max-w-md text-center text-xl text-black md:text-3xl"
            keypath="dna-testing.select_country"
            tag="h2"
            scope="global"
         >
            <template v-slot:step>
               <strong>{{ $t("dna-testing.step", { step_number: "2" }) }}</strong>
            </template>
         </i18n-t>

         <!-- Country-specific instructions for returning the sample -->
         <Instructions />
      </section>
   </main>
</template>

<script setup lang="ts">
import { Head } from "@unhead/vue/components";
import YoutubeVideo from "@/components/ui/YoutubeVideo.vue";
import Instructions from "./Instructions.vue";
import { useRoute } from "vue-router";
import { useToastKey } from "@/plugins/useToast";
import { useI18n } from "@/vue-i18n/vue-i18n";

const $route = useRoute();
const useToast = inject(useToastKey);
const { t: $t } = useI18n();

// onMounted
// -----------------------------------------
onMounted(() => {
   // when the query parameter 'kit_registered' is set, regardless of its value, show a toast with a "successful kit registration" message
   if ($route.query.kit_registered !== undefined) {
      useToast({ title: $t("dna-testing.kit_registered_message"), type: "success", position: "center" });
   }
});
</script>

<style scoped></style>
